import type { Props } from "tippy.js";
import { canv } from "@/canv/global";
import {
    commentResolveIsDisabled,
    generateUID,
    getBoolProperty,
    getNumberProperty,
    getStringProperty,
} from "@/canv/utils";
import { toScreenCoords } from "@/canvasController/helpers";
import { CONSTRUCTORS_WITHOUT_ANCHORS } from "@/constants";
import { ElementBase } from "@/elementBase";
import type { CommentsPopoverMode } from "@/elements/comment/CommentsPopovers";
import { generateColor } from "@/helpers/generateColor";
import { getInitials } from "@/helpers/getInitials";
import { transformManager } from "@/tools/cursorTool";
import { queryAreaByGroup } from "@/utils";
import type {
    CommentMessage,
    CommentStateFixType,
    CommentStateFull,
    CommentStateMap,
    CommentStateMove,
} from "./types";
import {
    findInsertIndex,
    getAnswersCount,
    getComment,
    getCommentList,
    getFooter,
    getHeader,
    handleSelectionChange,
    initNestedTooltips,
} from "./utils";

const COMMENT_SVG = `
    <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g filter="url(#filter0_d_120_7842)">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M25 8.0016C16.1632 8.0016 9 15.1647 9 24.0014C8.99865 26.3588 9.51823 28.6873 10.5216 30.8205L9.0336 38.0796C8.98062 38.3388 8.99274 38.6071 9.06887 38.8604C9.145 39.1138 9.28276 39.3443 9.46982 39.5314C9.65689 39.7184 9.88741 39.8562 10.1408 39.9323C10.3941 40.0084 10.6624 40.0206 10.9216 39.9676L18.1808 38.4796C20.2512 39.4556 22.5648 39.9996 25 39.9996C33.8368 39.9996 41 32.8381 41 23.9998C41 15.1647 33.8368 8 25 8" fill="white"/>
        </g>
        <defs>
            <filter id="filter0_d_120_7842" x="0" y="0" width="50" height="50" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="1"/>
                <feGaussianBlur stdDeviation="4.5"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0.79941 0 0 0 0 0.79941 0 0 0 0 0.79941 0 0 0 1 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_120_7842"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_120_7842" result="shape"/>
            </filter>
        </defs>
    </svg>
`;

const RESOLVED_SVG = `
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_670_4089)">
            <circle cx="6" cy="6" r="6" fill="white"/>
            <path d="M1 6C1 4.67392 1.52678 3.40215 2.46447 2.46447C3.40215 1.52678 4.67392 1 6 1C7.32608 1 8.59785 1.52678 9.53553 2.46447C10.4732 3.40215 11 4.67392 11 6C11 7.32608 10.4732 8.59785 9.53553 9.53553C8.59785 10.4732 7.32608 11 6 11C4.67392 11 3.40215 10.4732 2.46447 9.53553C1.52678 8.59785 1 7.32608 1 6ZM8.354 5.104C8.40049 5.05751 8.43736 5.00232 8.46252 4.94158C8.48768 4.88084 8.50063 4.81574 8.50063 4.75C8.50063 4.68426 8.48768 4.61916 8.46252 4.55842C8.43736 4.49768 8.40049 4.44249 8.354 4.396C8.30751 4.34951 8.25232 4.31264 8.19158 4.28748C8.13084 4.26232 8.06574 4.24937 8 4.24937C7.93426 4.24937 7.86916 4.26232 7.80842 4.28748C7.74768 4.31264 7.69249 4.34951 7.646 4.396L5.5 6.543L4.354 5.396C4.30751 5.34951 4.25232 5.31264 4.19158 5.28748C4.13084 5.26232 4.06574 5.24937 4 5.24937C3.93426 5.24937 3.86916 5.26232 3.80842 5.28748C3.74768 5.31264 3.69249 5.34951 3.646 5.396C3.59951 5.44249 3.56264 5.49768 3.53748 5.55842C3.51232 5.61916 3.49937 5.68426 3.49937 5.75C3.49937 5.81574 3.51232 5.88084 3.53748 5.94158C3.56264 6.00232 3.59951 6.05751 3.646 6.104L5.146 7.604C5.19245 7.65056 5.24762 7.68751 5.30837 7.71271C5.36911 7.73792 5.43423 7.75089 5.5 7.75089C5.56577 7.75089 5.63089 7.73792 5.69163 7.71271C5.75238 7.68751 5.80755 7.65056 5.854 7.604L8.354 5.104Z" fill="#2C2D32"/>
        </g>
        <defs>
            <clipPath id="clip0_670_4089">
                <rect width="12" height="12" rx="6" fill="white"/>
            </clipPath>
        </defs>
    </svg>
`;

let cachedSvgImage: HTMLImageElement | null = null;
let cachedResolvedSvgImage: HTMLImageElement | null = null;
let cachedIconLoading = true;
let cachedResolveLoading = true;

export class CommentWidget extends ElementBase {
    constructor(
        public startX = 0,
        public startY = 0,
    ) {
        super();
    }

    getSvgContent(): string {
        return COMMENT_SVG;
    }

    getSvgResolvedContent(): string {
        return RESOLVED_SVG;
    }

    shouldDraw() {
        const shouldDraw = this.parent?.isGroupOnScreen();
        if (!shouldDraw) {
            this.hideCommentPopovers();
        }

        return shouldDraw;
    }

    isCommentPopoverOpen(mode?: CommentsPopoverMode) {
        if (!mode) {
            return this.isCommentPopoverOpen("preview") || this.isCommentPopoverOpen("window");
        }

        // проверки достаточно, так как при скрытии uid присваивается пустая строка
        return canv.commentsPopovers[mode].UID === this.UID;
    }

    hideCommentPopovers = (mode?: CommentsPopoverMode) => {
        if (!mode) {
            this.hideCommentPopovers("preview");
            this.hideCommentPopovers("window");
            return;
        }

        if (canv.commentsPopovers[mode].UID === this.UID) {
            canv.actionManager.addContextmenuListener();
            canv.commentsPopovers.hideCommentsPopovers(mode);
        }
    };

    initCommentPopover = (mode: CommentsPopoverMode) => {
        const props: Partial<Props> = {
            content: this.getPopoverContent(mode),
            getReferenceClientRect: this.getReferenceClientRect,
        };

        if (mode === "window") {
            props.onShow = initNestedTooltips;
            props.onAfterUpdate = initNestedTooltips;
        }

        canv.commentsPopovers.initCommentsPopover(mode, this.UID, props);
    };

    getReferenceClientRect = () => {
        const { x, y } = toScreenCoords(this.startX, this.startY);

        return {
            width: 0,
            height: 0,
            left: x,
            x: x,
            y: y,
            top: y,
            right: x,
            bottom: y,
            toJSON() {
                return this;
            },
        };
    };

    updatePopover = (mode?: CommentsPopoverMode, withContent = false) => {
        if (!mode) {
            this.updatePopover("preview");
            this.updatePopover("window");
            return;
        }

        if (!canv.commentsPopovers[mode].instance) return;

        if (withContent) {
            canv.commentsPopovers[mode].instance.setContent(this.getPopoverContent(mode));
        }
        canv.commentsPopovers[mode].instance.popperInstance?.update();
    };

    getPopoverContent = (mode: CommentsPopoverMode) => {
        const fragment = document.createDocumentFragment();

        if (mode === "preview") {
            // TODO: добавить plural-формы
            fragment.appendChild(
                getComment(
                    {
                        name: this.accountName,
                        date: this.createdAt,
                        text: this.text,
                        authorUid: this.token,
                        uid: this.UID,
                    },
                    false,
                    this.UID,
                ),
            );

            if (this.messages?.length > 0) {
                fragment.appendChild(getAnswersCount(this.messages.length));
            }

            return fragment;
        }

        const onClose = () => {
            canv.actionManager.addContextmenuListener();
            this.hideCommentPopovers("window");
            document.removeEventListener("selectionchange", handleSelectionChange);
            canv.appInterface.inputFocused = 0;
            this.hideCommentPopovers("window");
        };

        const isResolveDisabled = commentResolveIsDisabled();
        const isProUser = canv.getInitDataProp("isProfessional");
        const showDelete = isProUser && canv.isBoardModeEditOrOwner();

        fragment.appendChild(
            getHeader({
                uid: this.UID,
                isResolved: this.isResolved,
                isResolveDisabled,
                showResolve: isProUser,
                showDelete,
                onClose,
                onResolve: () => {
                    if (isResolveDisabled) return;

                    onClose();
                    this.setResolved(!this.isResolved);
                },
                onDelete: () => {
                    canv.canvasController.deleteWidget(this.UID);
                },
            }),
        );

        fragment.appendChild(
            getCommentList(
                {
                    accountName: this.accountName,
                    createdAt: this.createdAt,
                    text: this.text,
                    authorUid: this.token,
                    uid: this.UID,
                },
                this.UID,
                this.messages,
            ),
        );
        document.addEventListener("selectionchange", handleSelectionChange);
        canv.actionManager.removeContextmenuListener();

        if (
            isProUser &&
            !this.isResolved &&
            (canv.isBoardModeEditOrOwner() || canv.isBoardModeEditMini())
        ) {
            fragment.appendChild(getFooter(this.createMessage));
        }

        return fragment;
    };

    checkIsHover() {
        if (canv.commentsPopovers.window.UID) return;

        const isHoverCommentIcon =
            this.isOver() &&
            !this.moved &&
            canv.toolBox.currentTool?.name === "cursorTool" &&
            !canv.actionManager.isMouseDown();

        if (isHoverCommentIcon) {
            if (!canv.commentsPopovers.preview.instance) {
                this.initCommentPopover("preview");
            } else {
                if (canv.commentsPopovers.preview.UID !== this.UID) {
                    canv.commentsPopovers.setPopoverProps("preview", this.UID, {
                        content: this.getPopoverContent("preview"),
                        getReferenceClientRect: this.getReferenceClientRect,
                    });
                }
            }

            canv.commentsPopovers.preview.instance?.show();
        } else {
            if (!canv.commentsPopovers.preview.UID) return;

            this.hideCommentPopovers("preview");
        }
    }

    isInteraction(context: string) {
        if (context === "click") {
            if (this.isOver()) {
                return { type: "interaction" };
            }

            if (this.isCommentPopoverOpen("window")) {
                this.hideCommentPopovers("window");
            }
        }

        return { type: "none" };
    }

    openPopoverWindow() {
        if (!canv.commentsPopovers.window.instance) {
            this.initCommentPopover("window");
        } else {
            if (canv.commentsPopovers.window.UID !== this.UID) {
                canv.commentsPopovers.setPopoverProps("window", this.UID, {
                    content: this.getPopoverContent("window"),
                    getReferenceClientRect: this.getReferenceClientRect,
                });
            }
        }

        this.hideCommentPopovers("preview");
        canv.commentsPopovers.window.instance?.show();
        this.scrollToLastComment();
    }

    TryAction(type: string): void {
        const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");

        if (type === "mouseup") {
            cursorTool?.turnMode("");

            if (!this.moved) {
                this.openPopoverWindow();
            } else {
                if (!this.parent) return;

                canv.hoverArea = null;

                const foundCandidates = queryAreaByGroup(
                    this.parent,
                    true,
                    false,
                    CONSTRUCTORS_WITHOUT_ANCHORS,
                );

                const oldAnchorUID = this.anchorUID;
                if (foundCandidates.length) {
                    this.fixState("anchor");
                    const group = foundCandidates[foundCandidates.length - 1];
                    this.anchorUID = group.children[0].UID;
                } else {
                    if (this.anchorUID) {
                        this.fixState("anchor");
                        this.anchorUID = "";
                    }
                }

                canv.eventManager.openRecord();
                this.saveState("move");
                if (oldAnchorUID !== this.anchorUID) {
                    this.saveState("anchor");
                }
                canv.eventManager.closeRecord();
            }

            this.moved = false;
        } else if (type === "mousemove" && cursorTool.mode === "interaction") {
            if (this.isCommentPopoverOpen("window")) {
                this.hideCommentPopovers("window");
            }
            if (!this.moved) {
                this.fixState("move");
            }

            this.moved = true;
            transformManager.actionAndMag("translate");
            canv.hoverArea = {
                x: this.startX,
                y: this.startY,
                width: this.width || 1,
                height: this.height || 1,
            };
        }
    }

    translate(x: number, y: number): void {
        this.startX += x;
        this.startY += y;

        this.updateDims();
    }

    getWidgetOptions() {
        return [];
    }

    setResolved(value: boolean) {
        canv.eventManager.openRecord();
        this.fixState("full");
        this.isResolved = value;
        this.saveState("full");
        canv.eventManager.closeRecord();
    }

    getFixType(reason: string): CommentStateFixType {
        switch (reason) {
            case "move":
                return "move";
            case "anchor":
                return "anchorUID";
            default:
                return "full";
        }
    }

    getState<T extends CommentStateFixType>(fixType: T): CommentStateMap[T] {
        switch (fixType) {
            case "full":
                return {
                    entityType: "comment",
                    UID: this.UID,
                    parentUID: this.parentUID,
                    startX: this.startX,
                    startY: this.startY,
                    isResolved: this.isResolved,
                    accountName: this.accountName,
                    createdAt: this.createdAt,
                    text: this.text,
                    token: this.token,
                    messages: this.messages,
                    updatedAt: this.updatedAt,
                    anchorUID: this.anchorUID,
                } as CommentStateMap[T];
            case "move":
                return {
                    startX: this.startX,
                    startY: this.startY,
                } as CommentStateMap[T];
            case "anchorUID":
                return {
                    anchorUID: this.anchorUID,
                } as CommentStateMap[T];
            default:
                return {} as never;
        }
    }

    loadState<T extends CommentStateFixType>(stateObj: CommentStateMap[T], fixType: T): void {
        if (fixType === "full") {
            const state = stateObj as CommentStateFull;
            this.UID = getStringProperty("UID", state);
            this.parentUID = getStringProperty("parentUID", state);
            this.startX = getNumberProperty("startX", state);
            this.startY = getNumberProperty("startY", state);
            this.isResolved = getBoolProperty("isResolved", state, false);
            this.accountName = getStringProperty("accountName", state);
            this.createdAt = getStringProperty("createdAt", state);
            this.updatedAt = getStringProperty("updatedAt", state);
            this.text = getStringProperty("text", state);
            this.token = getStringProperty("token", state);
            this.anchorUID = getStringProperty("anchorUID", state);
            // нужно ли так делать?
            this.messages = Array.isArray(state.messages)
                ? state.messages.map((message) => ({
                      UID: getStringProperty("UID", message),
                      createdAt: getStringProperty("createdAt", message),
                      token: getStringProperty("token", message),
                      accountName: getStringProperty("accountName", message),
                      text: getStringProperty("text", message),
                  }))
                : [];
        } else if (fixType === "move") {
            const state = stateObj as CommentStateMove;
            this.startX = getNumberProperty("startX", state);
            this.startY = getNumberProperty("startY", state);
        }
        this.updateDims();
    }

    updateDims(): void {
        this.updateBase(this.startX, this.startY, 0, 0);
    }

    isInArea(): boolean {
        return false;
    }

    isOver() {
        const size = canv.actionManager.getZoomConstant(this.commentSize);
        const { x, y } = canv.actionManager.getActionPoint();
        const halfSize = size / 2;
        const iconScreenX = this.baseStartX;
        const iconScreenY = this.baseStartY;

        return (
            x >= iconScreenX - halfSize &&
            x <= iconScreenX + halfSize &&
            y > iconScreenY - halfSize &&
            y <= iconScreenY + halfSize
        );
    }

    checkAndHideOfReconnect() {
        if (canv.appInterface.reconnectWindow.showed && canv.commentsPopovers.window.UID) {
            canv.commentsPopovers.hideCommentsPopovers("window");
        }
    }

    async loadSvgImage(
        cache: HTMLImageElement | null,
        blobItem: string,
    ): Promise<HTMLImageElement> {
        if (cache) {
            return Promise.resolve(cache);
        }

        return new Promise((resolve, reject) => {
            const img = new Image();
            const svgBlob = new Blob([blobItem], { type: "image/svg+xml" });
            const url = URL.createObjectURL(svgBlob);
            img.src = url;

            img.onload = () => {
                URL.revokeObjectURL(url);
                resolve(img);
            };
            img.onerror = (e) => reject(e);
        });
    }

    initCommentIcon() {
        if (cachedSvgImage || !cachedIconLoading) return null;
        cachedIconLoading = true;
        this.loadSvgImage(cachedSvgImage, this.getSvgContent())
            .then((img) => {
                cachedSvgImage = img;
            })
            .finally(() => {
                cachedIconLoading = false;
            });
    }

    initResolveIcon() {
        if (cachedResolvedSvgImage || !cachedResolveLoading) return null;
        cachedResolveLoading = true;
        this.loadSvgImage(cachedResolvedSvgImage, this.getSvgResolvedContent())
            .then((img) => {
                cachedResolvedSvgImage = img;
            })
            .finally(() => {
                cachedResolveLoading = false;
            });
    }

    drawInitialsIcon(accountName: string, color: string) {
        const dpr = canv.dpr;
        const zoom = canv.m[0];
        const visualCircleSize = 28;
        const visualIconSize = 50;
        const resolvedSize = 16;
        const circleRadius = visualCircleSize / 2;
        const iconSize = visualIconSize;

        this.initCommentIcon();

        canv.ctx.save();

        canv.ctx.translate(this.startX, this.startY);
        canv.ctx.scale(1 / zoom, 1 / zoom);

        if (cachedSvgImage) {
            canv.ctx.drawImage(
                cachedSvgImage,
                (-iconSize * dpr) / 2,
                (-iconSize * dpr) / 2,
                iconSize * dpr,
                iconSize * dpr,
            );
        }

        canv.ctx.beginPath();
        canv.ctx.arc(-0.2 * dpr, -1 * dpr, circleRadius * dpr, 0, Math.PI * 2);
        canv.ctx.fillStyle = color;
        canv.ctx.fill();

        const initials = getInitials(accountName);
        canv.ctx.fillStyle = "white";
        canv.ctx.textAlign = "center";
        canv.ctx.textBaseline = "middle";
        canv.ctx.font = `normal ${circleRadius * dpr}px sans-serif`;
        // TODO: заполнять текстом саму svg? fillText тяжелый, его лучше избегать
        canv.ctx.fillText(initials, -1 * dpr, -1 * dpr);

        if (this.isResolved) {
            this.initResolveIcon();
            if (cachedResolvedSvgImage) {
                canv.ctx.drawImage(
                    cachedResolvedSvgImage,
                    ((resolvedSize / 1.5) * dpr) / 2,
                    (-(iconSize - resolvedSize / 2) * dpr) / 2,
                    resolvedSize * dpr,
                    resolvedSize * dpr,
                );
            }
        }

        canv.ctx.restore();
    }

    async drawOver() {
        if (this.isNeedRender()) {
            this.checkIsHover();
            this.checkAndHideOfReconnect();
            const colorForUser = generateColor(this.token);
            this.drawInitialsIcon(this.accountName, colorForUser);
            this.updatePopover();
        }
    }

    createMessage = (commentText: string) => {
        const message = {
            UID: generateUID(),
            createdAt: new Date().toISOString(),
            token: canv.getInitDataProp("userToken"),
            accountName: canv.getInitDataProp("userName"),
            text: commentText,
        };

        canv.eventManager.openRecord();
        canv.eventManager.createPropertyBuildEvent(this.UID, "messages", "add", message);
        canv.eventManager.closeRecord();
    };

    deleteMessage = (messageUID: string) => {
        canv.eventManager.openRecord();
        canv.eventManager.createPropertyBuildEvent(this.UID, "messages", "remove", {
            UID: messageUID,
        });
        canv.eventManager.closeRecord();
    };

    addMessage(message: CommentMessage) {
        const idx = findInsertIndex(this.messages, message);
        this.messages.splice(idx, 0, message);
        this.updatedAt = this.messages[this.messages.length - 1]?.createdAt || this.createdAt;

        if (this.isCommentPopoverOpen("window")) {
            this.addMessageToList(message, idx);
        }
    }

    removeMessage(messageUid: CommentMessage["UID"]) {
        this.messages = this.messages.filter(({ UID }) => UID !== messageUid);
        this.updatedAt = this.messages[this.messages.length - 1]?.createdAt || this.createdAt;

        if (this.isCommentPopoverOpen("window")) {
            this.removeMessageFromList(messageUid);
        }
    }

    scrollToLastComment(commentsList?: HTMLDivElement) {
        requestAnimationFrame(() => {
            const list =
                commentsList ||
                canv.commentsPopovers.window.instance?.popper?.querySelector(".comment-list");
            list?.lastElementChild?.scrollIntoView({ behavior: "smooth", block: "end" });
        });
    }

    isNeedRender() {
        return !(
            !canv.canvasController.showComments ||
            (this.isResolved && !canv.canvasController.showResolvedComments)
        );
    }

    addMessageToList(message: CommentMessage, idx: number) {
        const list = canv.commentsPopovers.window.instance?.popper.querySelector(
            ".comment-list",
        ) as HTMLDivElement;
        if (!list) return;

        const comment = getComment(
            {
                name: message.accountName,
                date: message.createdAt,
                text: message.text,
                authorUid: message.token,
                uid: message.UID,
            },
            true,
            this.UID,
        );

        // главный коммент всегда в начале списка (0-й элемент), поэтому все рабочие индексы +1
        const nodes = list.children;
        const domIdx = idx + 1;

        if (domIdx >= nodes.length) {
            list.appendChild(comment);
        } else {
            list.insertBefore(comment, nodes[domIdx]);
        }

        this.scrollToLastComment(list);
    }

    removeMessageFromList(messageUid: CommentMessage["UID"]) {
        const comment = canv.commentsPopovers.window.instance?.popper.querySelector(
            `.comment[data-uid="${messageUid}"]`,
        );
        if (!comment) return;

        comment.parentNode?.removeChild(comment);
    }

    beforeDeletion() {
        this.hideCommentPopovers();
    }

    constructorName: "comment" = "comment";
    rotatable = false;
    fastAction = "";

    public isResolved = false;
    public text = "";
    public accountName = "";
    public createdAt = "";
    public updatedAt = "";
    public token = "";
    public messages: CommentMessage[] = [];
    public width = 0;
    public height = 0;

    private commentSize = 50;
    private moved = false;
}
